/**
 * **A port of `legacy/frontend/src/screens/program.ts`, whole but for its `:1`.** F-024 T4: the
 * vocabulary of a programme — `KIND_WORD`, `NOT_DONE` and `subLine` — shared by «Программа» and
 * the closed service's block on «Что прошло».
 *
 * Their `:1` is a type-only import of `ProgramItemKind` and `ProgramItemRow` from `../api`. Both
 * are server shapes, so here they come from `@contracts/index` (ADR-0011). Their `:2` to the end
 * is theirs byte for byte, and `scripts/ported-partial.txt` holds that region.
 */

import type { ProgramItemKind, ProgramItemRow } from '@contracts/index';

/**
 * The vocabulary of a programme, shared by the two screens that draw its
 * numbers: «Программа» itself, where they are signed up, and the closed
 * service on «Что прошло», where what happened to them is recorded.
 *
 * It lives here rather than in either screen because a second copy is how one
 * list ends up calling a verse «стих» and the other «стихотворение».
 */
export const KIND_WORD: Record<ProgramItemKind, string> = {
  song: 'песня',
  verse: 'стих',
  sermon: 'проповедь',
  testimony: 'свидетельство',
};

/**
 * The verb for «this one did not happen», per kind.
 *
 * Four words and not one, because «не спели» on a verse is a word about the
 * wrong thing — and the mark carries no object of its own, so the verb is all
 * the row says out loud. A sermon and a testimony share «не было»: neither is
 * sung nor read, and Russian has no shorter honest verb that covers both.
 */
export const NOT_DONE: Record<ProgramItemKind, string> = {
  song: 'не спели',
  verse: 'не читали',
  sermon: 'не было',
  testimony: 'не было',
};

/**
 * The line under a number: what it is, whose it is, and who sings it.
 *
 * A choir number is not captioned «поёт весь хор»: the pill beside the title
 * already says that, and the word twice in one line is the app repeating
 * itself. What the caption says about it is the same thing it says about
 * everything else — the kind, and who put it in the list.
 */
export function subLine(item: ProgramItemRow): string {
  const parts = [KIND_WORD[item.kind], item.is_mine ? 'это ты' : item.display_name];
  if (item.singers.length > 0) {
    parts.push(`поют ${item.singers.map((one) => one.display_name).join(', ')}`);
  }
  return parts.join(' · ');
}
